import { useSearchMembers } from "@app/lib/swr/memberships";
import type {
  LightUserType,
  LightUserTypeWithWorkspace,
  LightWorkspaceType,
  UserTypeWithWorkspace,
} from "@app/types/user";
import {
  Avatar,
  createSelectionColumn,
  DataTable,
  EmptyCTA,
  SearchInput,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  CellContext,
  ColumnDef,
  PaginationState,
  RowSelectionState,
} from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";

export type SearchMemberType =
  | LightUserType
  | LightUserTypeWithWorkspace
  | UserTypeWithWorkspace;

export type SearchMemberWithWorkspaceType =
  | LightUserTypeWithWorkspace
  | UserTypeWithWorkspace;

export function isFullUserType(
  user: SearchMemberType
): user is UserTypeWithWorkspace {
  return "lastLoginAt" in user;
}

export interface MemberRowData {
  sId: string;
  fullName: string;
  image: string;
  email?: string;
  onClick?: () => void;
}

function getMemberTableRows(members: SearchMemberType[]): MemberRowData[] {
  return members.map((user) => ({
    sId: user.sId,
    fullName: user.fullName,
    image: user.image ?? "",
    email: user.email,
  }));
}

interface MemberSelectionTableProps {
  owner: LightWorkspaceType;
  selectedMemberIds: Set<string>;
  onSelectionChange: (ids: Set<string>, users: SearchMemberType[]) => void;
  extraColumns?: ColumnDef<MemberRowData>[];
  initialMembers?: SearchMemberType[];
  disabled?: boolean;
}

export function MemberSelectionTable({
  owner,
  selectedMemberIds,
  onSelectionChange,
  extraColumns,
  initialMembers,
  disabled = false,
}: MemberSelectionTableProps) {
  const { t } = useLingui();
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { members, totalMembersCount, isLoading } = useSearchMembers({
    workspaceId: owner.sId,
    searchTerm: searchText,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    disabled: !searchText,
  });

  // Internal map to resolve sId -> SearchMemberType, seeded with initialMembers
  // and updated as search results come in.
  const userMapRef = useRef(
    new Map<string, SearchMemberType>(
      (initialMembers ?? []).map((m) => [m.sId, m])
    )
  );

  // Update the map synchronously so downstream memos read fresh data.
  for (const member of members) {
    if (!userMapRef.current.has(member.sId)) {
      userMapRef.current.set(member.sId, member);
    }
  }

  // When not searching, show selected members; when searching, show search results.
  const rows = useMemo(() => {
    if (!searchText) {
      const selectedUsers = Array.from(selectedMemberIds)
        .map((sId) => userMapRef.current.get(sId))
        .filter((u): u is SearchMemberType => !!u);
      return getMemberTableRows(selectedUsers);
    }
    return getMemberTableRows(members);
  }, [searchText, selectedMemberIds, members]);

  const rowSelectionState: RowSelectionState = useMemo(
    () =>
      Object.fromEntries(Array.from(selectedMemberIds, (sId) => [sId, true])),
    [selectedMemberIds]
  );

  const handleRowSelectionChange = (newSelection: RowSelectionState) => {
    if (disabled) {
      return;
    }
    const newIds = new Set(
      Object.entries(newSelection)
        .filter(([, selected]) => selected)
        .map(([sId]) => sId)
    );

    const users: SearchMemberType[] = [];
    for (const sId of newIds) {
      const user = userMapRef.current.get(sId);
      if (user) {
        users.push(user);
      }
    }

    onSelectionChange(newIds, users);
  };

  const columns: ColumnDef<MemberRowData>[] = useMemo(() => {
    return [
      createSelectionColumn<MemberRowData>({
        hideSelectAll: !!searchText && rows.length <= 1,
      }),
      {
        accessorKey: "fullName",
        header: t`Name`,
        id: "fullName",
        sortingFn: "text",
        meta: {
          className: "w-full",
        },
        cell: (info: CellContext<MemberRowData, unknown>) => {
          const { fullName, image, email } = info.row.original;
          return (
            <DataTable.CellContent>
              <div className="flex items-center gap-2">
                <Avatar
                  name={fullName}
                  visual={image || undefined}
                  size="xs"
                  isRounded
                />
                <div className="flex flex-col">
                  <span className="text-sm">{fullName}</span>
                  {email && (
                    <span className="text-xs text-muted-foreground">
                      {email}
                    </span>
                  )}
                </div>
              </div>
            </DataTable.CellContent>
          );
        },
      },
      ...(extraColumns ?? []),
    ];
  }, [extraColumns, rows.length, searchText, t]);

  return (
    <>
      <SearchInput
        name="member-search"
        value={searchText}
        onChange={handleSearchChange}
        placeholder={t`Search users...`}
        className="mt-2"
      />
      <div className="flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <span className="text-sm text-muted-foreground">
              <Trans>Loading users...</Trans>
            </span>
          </div>
        ) : initialMembers?.length === 0 &&
          selectedMemberIds.size === 0 &&
          searchText === "" ? (
          <EmptyCTA
            title={t`This group has no members`}
            message={t`Add members to the group by searching for them above.`}
            action={null}
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            pagination={pagination}
            setPagination={setPagination}
            totalRowCount={searchText ? totalMembersCount : rows.length}
            rowSelection={rowSelectionState}
            setRowSelection={handleRowSelectionChange}
            enableRowSelection={!disabled}
            getRowId={(row) => row.sId}
          />
        )}
      </div>
    </>
  );
}
